/**
 * LE SUIVI D'UNE DEMANDE — commentaires, réponses, et journal.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Amené en Qualité le 13/09/2026, avec le reste du traitement des demandes de
 * création de compte. Ce module n'invente rien : il lit et écrit EXACTEMENT
 * les mêmes enregistrements que l'inbox du site lisait avant (`demande-notes`,
 * `audit-logs`). C'était la condition pour que « un seul endroit » ne veuille
 * pas dire « une capacité en moins » — mesuré avant la bascule : 6 demandes en
 * base, toutes refusées, et 1 commentaire interne qui serait devenu invisible.
 *
 * ⚠️ L'AJOUT D'UNE NOTE PASSE PAR `fetchEcriture`, comme toute écriture de la
 * Qualité. C'est l'enrobage qui alimente le témoin « enregistrement en
 * cours » ; un `fetch` nu ici n'aurait rien cassé de visible — et c'est bien
 * le problème : la fenêtre se serait fermée sans que rien ne signale qu'un
 * envoi était en vol. Le contrôle `qualite-ecriture-enrobee` relit ce fichier.
 *
 * ⚠️ CE MODULE NE DÉCIDE RIEN. Approuver, refuser et remettre en attente
 * vivent dans `lib/team.ts`, parce que ces trois gestes touchent au COMPTE.
 * Ici, on ne fait qu'annoter et relire. Les mélanger ferait un module qui, un
 * jour, écrirait un statut « au passage ».
 */

/** Une ligne de suivi.
 *  · `commentaire` = note interne, invisible du demandeur
 *  · `reponse`     = message ENVOYÉ au demandeur
 *  · `entrant`     = message REÇU du demandeur, déposé par la relève IMAP.
 *    ⚠️ `entrant` ne peut pas être créé depuis un écran : le contrôleur Strapi
 *    n'accepte que les deux premiers. Une réponse entrante qu'on pourrait
 *    fabriquer à la main ne serait plus une preuve de rien. */
export interface NoteDemande {
  documentId: string;
  cible: 'rdv' | 'compte';
  cibleRef: string;
  type: 'commentaire' | 'reponse' | 'entrant';
  texte: string;
  auteur?: string;
  destinataire?: string;
  /** Sort du courriel, pour une `reponse` : `echec` = elle est enregistrée,
   *  mais le demandeur ne l'a jamais reçue. L'écran doit le DIRE. */
  envoi?: 'aucun' | 'envoye' | 'echec';
  createdAt?: string;
}

export interface LigneJournal {
  ts: string;
  who: string;
  action: string;
  detail: string;
  ficheCode?: string;
  createdAt?: string;
}

import { fetchEcriture } from '@/lib/ecrire';

async function lire(res: Response) {
  const d = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = d?.error?.message || d?.error || 'Erreur serveur';
    throw new Error(typeof msg === 'string' ? msg : 'Erreur serveur');
  }
  return d;
}

/** Le suivi d'UNE demande, du plus ancien au plus récent (l'ordre du serveur). */
export async function listerNotes(cibleRef: string): Promise<NoteDemande[]> {
  const d = await lire(await fetch(`/api/inbox/demande-notes?cibleRef=${encodeURIComponent(cibleRef)}`, { credentials: 'include' }));
  return d?.data ?? [];
}

/**
 * Ajoute un commentaire interne, ou envoie une réponse au demandeur.
 *
 * ⚠️ Le retour porte DEUX faits : la note (toujours enregistrée) et `envoi`.
 * Une réponse dont le courriel a échoué est conservée quand même — la perdre
 * parce que l'envoi a raté effacerait le travail de quelqu'un — mais l'écran
 * ne doit surtout pas la présenter comme partie.
 */
export async function ajouterNote(entree: { cible: 'rdv' | 'compte'; cibleRef: string; type: 'commentaire' | 'reponse'; texte: string }) {
  const d = await lire(await fetchEcriture('/api/inbox/demande-notes', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ data: entree }),
  }));
  return d as { data: NoteDemande; envoi: 'aucun' | 'envoye' | 'echec' };
}

/** Le journal d'UNE demande — filtré PAR LE SERVEUR sur sa référence.
 *  Filtrer dans le navigateur ne verrait que les dernières entrées du registre
 *  et raterait celles d'une demande ouverte il y a longtemps. */
export async function journalDe(ficheCode: string): Promise<LigneJournal[]> {
  const d = await lire(await fetch(`/api/audit?ficheCode=${encodeURIComponent(ficheCode)}`, { credentials: 'include' }));
  return d?.data ?? [];
}
